import { Align, Arrange, Container, Loader, Spacer } from '@loomhq/lens';
import React, { useCallback, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import { updateShareVideoRecordingVersion } from 'actions/shareVideo';
import { updateShowTrimExitWarning } from 'actions/trimming';
import {
  mergeVideoProcessingInformation,
  requestVideoPlayerCurrentTime,
  updateVideoPlayerDisplayedDuration,
  updateVideoPlayerSourceFromClip,
  updateVideoPlayerVideoRecordingVersion,
} from 'actions/videoPlayer';
import {
  EDIT_CLICK_SPLIT_AT,
  EDIT_DELETE_ADD_CLIP,
  TRIM_CANCELLED,
} from 'constants/events';
import { VIDEO_DURATION } from 'constants/limits';
import { V6 } from 'constants/recordingVersions';
import { MIN_PLAY_RANGE_DURATION } from 'constants/trimming';
import { leaveEditModeCreator } from 'creators/trimming';
import { repositionPlayHeadInPlayRange } from 'creators/videoPlayer';
import { selectVideoId } from 'selectors/video';
import {
  selectPlayerTime,
  selectRequestedPlayerTime,
  selectVideo,
  selectVideoDuration,
  selectVideoPlayerIsFetching,
  selectVideoPlayerReadyToPlayOnSharePage,
} from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { getRelativePosition } from 'utilities/dom';
import { getGraphQLClient } from 'utilities/graphql';
import { findCurrentPlayRangeIdx } from 'utilities/trimming';

import { useRoleLimit } from 'hooks/useRoleLimit';
import { useWorkspaceLimit } from 'hooks/useWorkspaceLimit';

import ClipEditor from './clip-editor';
import TrimControlBar from './control-bar';
import EditInformation from './edit-information';
import { useClipsState } from './clips/state';
import { DeleteDraftClips } from './graphql/DeleteDraftClips.gql';
import { PrepareForEdit } from './graphql/PrepareForEdit.gql';
import { useEditToolsKeyboardShortcuts, useIsOriginalVideoClip } from './hooks';
import { SECONDS_TO } from '../../../../../shared/constants/units';
import { Video } from '../../../../../shared/types/clips';

import { findClosestPlayRangeAndLoc } from 'components/share-video/edit-tools/clips/util';
import styles from 'components/share-video/edit-tools/styles.module.less';

const FIVE_HOUR_MAX_LIMIT_SECONDS = 5 * SECONDS_TO.HOURS;
const MIN_CLIP_FRACTION_SIZE = 0.05;

const EditTools = (): JSX.Element => {
  const {
    // state
    clips,
    currentClip,
    duration,
    focusedClipIndex,
    focusedPlayRangeIndex,
    hasBeenUpdated,
    hoveredClipId,
    originalVideoHash,
    playRanges,
    recentlyAddedInfo,
    trimVideoDuration,

    // functions
    addClips,
    addOriginalVideoHash,
    canDeletePlayRange,
    canRedo,
    canUndo,
    getClipTimeOffset,
    getDuration,
    getTrimVideoDuration,
    hasTrims,
    redo,
    removeClip,
    removePlayRangeFromClip,
    repositionClip,
    splitCurrentPlayRange,
    undo,
    updateCurrentClipDuration,
    updateFocusedClipIndex,
    updateFocusedPlayRangeIndex,
    updateHoveredClipId,
    setShowRevertWarning,
  } = useClipsState(state => ({
    // state
    clips: state.clips,
    currentClip: state.getCurrentClip(),
    currentPlayRange: state.getCurrentPlayRanges()[state.focusedPlayRangeIndex],
    duration: state.getDuration(),
    focusedClipIndex: state.focusedClipIndex,
    focusedPlayRangeIndex: state.focusedPlayRangeIndex,
    hasBeenUpdated: state.hasBeenUpdated,
    hoveredClipId: state.hoveredClipId,
    originalVideoHash: state.originalVideoHash,
    playRanges: state.getCurrentPlayRanges(),
    recentlyAddedInfo: state.recentlyAddedInfo,
    trimVideoDuration: state.getTrimVideoDuration(),

    // functions
    addClips: state.addClips,
    addOriginalVideoHash: state.addOriginalVideoHash,
    canDeletePlayRange: state.canDeletePlayRange,
    canRedo: state.canRedo,
    canUndo: state.canUndo,
    getClipTimeOffset: state.getClipTimeOffset,
    getDuration: state.getDuration,
    getTrimVideoDuration: state.getTrimVideoDuration,
    hasTrims: state.hasTrims,
    redo: state.redo,
    removeClip: state.removeClip,
    removePlayRangeFromClip: state.removePlayRangeFromClip,
    repositionClip: state.repositionClip,
    splitCurrentPlayRange: state.splitCurrentPlayRange,
    undo: state.undo,
    updateCurrentClipDuration: state.updateCurrentClipDuration,
    updateFocusedClipIndex: state.updateFocusedClipIndex,
    updateFocusedPlayRangeIndex: state.updateFocusedPlayRangeIndex,
    updateHoveredClipId: state.updateHoveredClipId,

    setShowRevertWarning: state.setShowRevertWarning,
  }));

  const dispatch = useDispatch();
  const editorsContainerRef = useRef<HTMLDivElement>(null);
  const currentTime: number = useSelector(selectPlayerTime);
  const requestedCurrentTime = useSelector(selectRequestedPlayerTime);
  const videoDuration: number = useSelector(selectVideoDuration);
  const video: Video = useSelector(selectVideo);
  const videoId: string = useSelector(selectVideoId);

  const workspaceVideoDurationLimit = useWorkspaceLimit(VIDEO_DURATION);
  const memberVideoDurationLimit = useRoleLimit(VIDEO_DURATION);
  const videoDurationLimit =
    workspaceVideoDurationLimit || memberVideoDurationLimit;

  useEditToolsKeyboardShortcuts();

  const durationLimit = videoDurationLimit || FIVE_HOUR_MAX_LIMIT_SECONDS;

  const isOverVideoDurationLimits = getTrimVideoDuration() > durationLimit;

  // TODO fix/eliminate all following for weave
  const isFetching = useSelector(selectVideoPlayerIsFetching);
  const readyToPlay = useSelector(selectVideoPlayerReadyToPlayOnSharePage);

  const isOriginalVideoClip = useIsOriginalVideoClip(
    videoId,
    clips,
    originalVideoHash,
    video?.processing_information?.trim_id,
    video.video_properties.recording_version || ''
  );

  const selectedPlayRange = playRanges[focusedPlayRangeIndex] ?? null;
  const currentTimeInSelectedPlayRangeBounds = Boolean(
    selectedPlayRange &&
      currentTime - selectedPlayRange.from >= MIN_PLAY_RANGE_DURATION &&
      selectedPlayRange.to - currentTime >= MIN_PLAY_RANGE_DURATION
  );
  const canSplitPlayRange = Boolean(
    playRanges.length > 0 &&
      requestedCurrentTime == null &&
      currentTimeInSelectedPlayRangeBounds
  );

  // This effect is here b/c we do not have an accurate duration until the
  // video loads. The duration in video_player is the duration of the
  // trimmed video and the duration in video_player.video.video_properties
  // is rounded which can mess up our play range values. We will only update
  // the current clip duration if there is a change in the duration stored in
  // redux compared to the clips current duration is within 1 second.
  //
  // The updateDuration function is stored in a ref so we can update it only
  // once the focusedClipIndex has changed. Without that, it would be called
  // with the wrong focusedClipIndex and duration.
  const updateDurationFnRef = useRef<(videoDuration: number) => void>();

  useEffect(() => {
    updateDurationFnRef.current = videoDuration => {
      const currentDuration = currentClip?.duration || 0;
      const videoCurrentDurationDifference = Math.abs(
        videoDuration - currentDuration
      );

      if (
        clips.length > 0 &&
        focusedClipIndex != null &&
        Number(videoDuration) > 0 &&
        (currentDuration === 0 ||
          currentDuration == null ||
          (videoCurrentDurationDifference > 0 &&
            videoCurrentDurationDifference < 1))
      ) {
        updateCurrentClipDuration(videoDuration);
      }
    };
  }, [clips.length, focusedClipIndex, updateCurrentClipDuration, currentClip]);

  useEffect(() => {
    if (updateDurationFnRef.current) {
      updateDurationFnRef.current(videoDuration);
    }
  }, [videoDuration]);

  const editInformationContainerRef = useCallback(buttonsNode => {
    if (buttonsNode) {
      buttonsNode.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, []);

  const performUndo = () => {
    undo(videoId);
    dispatch(updateVideoPlayerDisplayedDuration(getDuration()));
  };

  const performRedo = () => {
    redo(videoId);
    dispatch(updateVideoPlayerDisplayedDuration(getDuration()));
  };

  useHotkeys(
    'cmd+z,ctrl+z',
    e => {
      e.preventDefault();
      performUndo();
    },
    { enabled: canUndo() }
  );

  useHotkeys(
    'cmd+shift+z,ctrl+shift+z',
    e => {
      e.preventDefault();
      performRedo();
    },
    {
      enabled: canRedo(),
    }
  );

  // Handle URL changes, back button, page reload with unsaved changes
  const unloadEventListenerRef = useRef(() => '');

  useEffect(() => {
    const removeEventListener = () => {
      if (unloadEventListenerRef.current) {
        window.removeEventListener(
          'beforeunload',
          unloadEventListenerRef.current,
          { capture: true }
        );
      }
    };

    const showWarningModal = event => {
      event.preventDefault();

      return (event.returnValue =
        'There are unsaved changes to your video. Are you sure you want to discard them?');
    };

    removeEventListener();

    if (hasBeenUpdated) {
      // @ts-expect-error ignore due to enabling strict null checks
      unloadEventListenerRef.current = showWarningModal;
      window.addEventListener('beforeunload', unloadEventListenerRef.current, {
        capture: true,
      });
    }

    return removeEventListener;
  }, [hasBeenUpdated, dispatch]);

  const prepareForEditing = useCallback(
    async (videoId, addClips, addOriginalVideoHash) => {
      const gqlClient = getGraphQLClient();
      const { data } = await gqlClient.mutate({
        mutation: PrepareForEdit,
        variables: {
          videoId,
        },
        fetchPolicy: 'no-cache',
      });

      if (data?.prepareForEdit?.message == null) {
        // the clips have just been created with the above call to PrepareForEdit
        // we need to add the clips to the (empty) state
        await addClips(data.prepareForEdit.video.clips, { initial: true });

        addOriginalVideoHash(data.prepareForEdit.video.original_hash, {
          initial: true,
        });

        const newRecordingVersion =
          data?.prepareForEdit?.video?.video_properties?.recording_version;

        const firstClip = data.prepareForEdit.video.clips.find(
          clip => clip?.position === 1 && clip?.source_url
        );

        if (firstClip) {
          dispatch(
            updateVideoPlayerSourceFromClip({
              sourceUrl: firstClip?.source_url,
              sourceMimeType: firstClip?.source_mime_type,
              partCredentials: firstClip?.source_part_credentials,
              sourceUrlIsTranscoded: firstClip?.source_url_is_transcoded,
              seekTime: 0,
              clipTimeOffset: 0,
            })
          );
        }

        dispatch(updateVideoPlayerDisplayedDuration(getDuration()));
        dispatch(updateVideoPlayerVideoRecordingVersion(newRecordingVersion));
        dispatch(updateShareVideoRecordingVersion(newRecordingVersion));

        const processingInformation =
          data?.prepareForEdit?.video?.processing_information;

        if (processingInformation) {
          dispatch(mergeVideoProcessingInformation(processingInformation));
        }
      } else {
        // TODO: handle error when no clips
      }
    },
    [dispatch, getDuration]
  );

  useEffect(() => {
    prepareForEditing(videoId, addClips, addOriginalVideoHash);
  }, [videoId, addClips, addOriginalVideoHash, prepareForEditing]);

  const updateSelectedPlayRange = () => {
    if (clips.length === 0) {
      return;
    }

    if (requestedCurrentTime != null) {
      return;
    }

    const selectedPlayRange = playRanges[focusedPlayRangeIndex];

    if (
      !selectedPlayRange ||
      currentTime < selectedPlayRange.from ||
      currentTime > selectedPlayRange.to
    ) {
      const idx = findCurrentPlayRangeIdx(currentTime, playRanges);

      if (idx !== -1) {
        updateFocusedPlayRangeIndex(idx);
      }
    }
  };

  useEffect(updateSelectedPlayRange, [
    currentTime,
    clips.length,
    playRanges,
    focusedPlayRangeIndex,
    requestedCurrentTime,
    updateFocusedPlayRangeIndex,
  ]);

  /* keep the playhead inside the play ranges */
  const placePlayheadInPlayRanges = () => {
    if (clips.length === 0) {
      return;
    }

    // If we are in a play range already or time is being updated
    if (requestedCurrentTime != null) {
      return;
    }

    if (playRanges.some(r => currentTime >= r.from && currentTime <= r.to)) {
      // the current time is already within a play range
      return;
    }

    // If we are not in a play range, move to the closest one
    const { closestLoc, playRangeIdx } = findClosestPlayRangeAndLoc(
      currentTime,
      playRanges
    );

    // if we're still be in the same play range
    if (playRangeIdx === focusedPlayRangeIndex) {
      return;
    }

    const lastPlayRangeTo = playRanges[playRanges.length - 1]?.to;

    if (lastPlayRangeTo === closestLoc) {
      // other code is already going to swap clips
      return;
    }

    dispatch(
      repositionPlayHeadInPlayRange({
        newPlayerTime: closestLoc,
        updateSelectedPlayRange: () =>
          updateFocusedPlayRangeIndex(playRangeIdx),
      })
    );
  };

  useEffect(placePlayheadInPlayRanges, [
    dispatch,
    clips.length,
    playRanges,
    currentTime,
    focusedPlayRangeIndex,
    updateFocusedPlayRangeIndex,
    requestedCurrentTime,
  ]);

  // commented out this piece of code as this does not let a user
  // click into a specific timestamp within a play range.
  // update playhead when focused clip or play range changes
  // useEffect(() => {
  //   if (currentPlayRange) {
  //     dispatch(requestVideoPlayerCurrentTime(currentPlayRange.from));
  //   }
  // }, [dispatch, clips?.length, currentClip?.id, currentPlayRange?.from]);

  const updatePlayheadAfterRemoveClip = removedClipIndex => {
    // one assumption here is that the removed clip is always the selected clip because of
    // the way the delete button works right now (it operates on the focused clip)
    let nextClip;
    let seekTime;
    let nextClipIndex;

    if (removedClipIndex < clips.length - 1) {
      nextClipIndex = removedClipIndex + 1;
      nextClip = clips[nextClipIndex];
      seekTime = nextClip.playRanges[0].from;
    } else {
      nextClipIndex = removedClipIndex - 1;
      nextClip = clips[nextClipIndex];
      seekTime = nextClip.playRanges[nextClip.playRanges.length - 1].to;
    }

    const clipTimeOffset = getClipTimeOffset(nextClipIndex);

    dispatch(
      updateVideoPlayerSourceFromClip({
        sourceUrl: nextClip?.source_url,
        sourceMimeType: nextClip?.source_mime_type,
        partCredentials: nextClip?.source_part_credentials,
        sourceUrlIsTranscoded: nextClip?.source_url_is_transcoded,
        seekTime,
        clipTimeOffset,
      })
    );
    dispatch(updateVideoPlayerDisplayedDuration(getDuration()));
    dispatch(requestVideoPlayerCurrentTime(seekTime));
  };

  const deletePlayRange = () => {
    // note: this function assumes the clip or play range being deleted is always the focused one
    analytics.track(EDIT_DELETE_ADD_CLIP, {
      id: videoId,
      has_been_trimmed: true,
      event_type: 'click',
    });

    if (!playRanges) {
      return;
    }

    if (playRanges.length <= 1) {
      // last play range in the clip, we should just remove the clip itself
      removeClip(focusedClipIndex);
      updatePlayheadAfterRemoveClip(focusedClipIndex);
      // remove the clip's duration from the total displayed
      dispatch(updateVideoPlayerDisplayedDuration(getDuration()));
    } else {
      removePlayRangeFromClip(focusedClipIndex, focusedPlayRangeIndex);
    }
  };

  useHotkeys('backspace, delete, del', deletePlayRange, [
    playRanges,
    focusedClipIndex,
    focusedPlayRangeIndex,
  ]);

  const splitPlayRange = () => {
    splitCurrentPlayRange(currentTime);

    analytics.track(EDIT_CLICK_SPLIT_AT, {
      id: videoId,
      type: 'split',
      hotkey: false,
      has_been_trimmed: hasTrims,
      event_type: 'click',
    });
  };

  useHotkeys('/', splitPlayRange, [currentTime]);

  const exitWithoutSaving = () => {
    if (hasBeenUpdated) {
      dispatch(updateShowTrimExitWarning(true));
    } else {
      goBackToVideo();
    }
  };

  const goBackToVideo = async () => {
    dispatch(leaveEditModeCreator());

    const gqlClient = getGraphQLClient();

    await gqlClient.mutate({
      mutation: DeleteDraftClips,
      variables: {
        rootVideoId: videoId,
      },
      fetchPolicy: 'no-cache',
    });

    analytics.track(TRIM_CANCELLED, {
      id: videoId,
      has_been_trimmed: hasTrims,
      event_type: 'click',
    });
  };

  const revertToOriginal = () => setShowRevertWarning(true);

  const isOriginalVideoHashLoaded = Boolean(originalVideoHash);
  const canRevertToOriginal = isOriginalVideoHashLoaded && !isOriginalVideoClip;

  const trimControlBarProps = {
    currentTime,
    canSplitPlayRange,
    splitPlayRange,
    canDeletePlayRange: canDeletePlayRange(),
    deletePlayRange,
    canUndo: canUndo(),
    canRedo: canRedo(),
    undo: performUndo,
    redo: performRedo,
    revertToOriginal,
    canRevertToOriginal,
    videoId,
  };

  const isReady =
    !isFetching &&
    /* readyToPlay OR if you are V6 we are going to temporarily
    load the page anyway instead of showing a loading spinner */
    (readyToPlay || video?.video_properties?.recording_version === V6);

  if (!isReady) {
    return (
      <Container paddingTop="xlarge">
        <Align alignment="center">
          <Loader size="large" />
        </Align>
      </Container>
    );
  }

  // TODO: Update this to be true when users have added clips but have not edited the duration
  // of any single clip
  const durationChanged = duration != trimVideoDuration;

  const allClipsDuration = clips.reduce(
    (acc, c) => acc + c.video_properties.duration,
    0
  );

  const handleClick = (
    event: React.MouseEvent,
    { secs, clipIdx }: { secs: number; clipIdx: number }
  ) => {
    if (clipIdx !== -1 && clipIdx < clips.length) {
      const clip = clips[clipIdx];

      for (const [rangeIdx, { from, to }] of clip.playRanges?.entries() || []) {
        if (secs >= from && secs <= to) {
          updateFocusedPlayRangeIndex(rangeIdx);
          dispatch(requestVideoPlayerCurrentTime(secs));
        }
      }
    }
  };

  const onClipReordering = (
    originalIdx: number,
    swapToRight: boolean
  ): void => {
    // the transition takes 300ms, we're giving it an extra 50ms to
    // ensure the last few frames of the animation get rendered, otherwise
    // there could be a noticeable jump at the end of the transition.
    const animationCompleteMs = 350;

    if (!editorsContainerRef.current) {
      return;
    }

    // find all clips affected by the move
    const editors = Array.from(
      (editorsContainerRef.current as HTMLElement).getElementsByClassName(
        styles.clipEditor
      )
    );

    const animCnt = document.createElement('div');

    animCnt.className = styles.animationContainer;

    const leftIndex = swapToRight ? originalIdx : originalIdx - 1;
    const rightIndex = swapToRight ? originalIdx + 1 : originalIdx;
    const leftEditor = editors[leftIndex] as HTMLElement;
    const rightEditor = editors[rightIndex] as HTMLElement;

    const leftCloneFinalPos =
      getRelativePosition(
        rightEditor,
        editorsContainerRef.current as HTMLElement
      ).left +
      rightEditor.clientWidth -
      leftEditor.clientWidth;
    const rightCloneFinalPos = getRelativePosition(
      leftEditor,
      editorsContainerRef.current as HTMLElement
    ).left;

    const leftCloneEl = createAnimatedClone(
      leftEditor,
      editorsContainerRef.current,
      animCnt
    );
    const rightCloneEl = createAnimatedClone(
      rightEditor,
      editorsContainerRef.current,
      animCnt
    );

    // hide the actual clip editors
    leftEditor.style.visibility = 'hidden';
    rightEditor.style.visibility = 'hidden';

    (editorsContainerRef.current as HTMLElement).appendChild(animCnt);

    // update position to final location. We're updating it after a timeout so that it gives the DOM
    // a change to add/render it first in the initial position then animate the transition to the
    // final position.
    setTimeout(() => {
      leftCloneEl.style.left = `${leftCloneFinalPos}px`;
      rightCloneEl.style.left = `${rightCloneFinalPos}px`;
    }, 0);

    setTimeout(() => {
      // animation done, we can move the actual clips to the new position and show them
      const newIdx = originalIdx + (swapToRight ? 1 : -1);

      // to avoid some jitter, hide the clones before removing them from the DOM
      animCnt.style.display = 'none';
      setTimeout(() => animCnt.remove(), 100);

      leftEditor.style.visibility = '';
      rightEditor.style.visibility = '';

      repositionClip(originalIdx, newIdx);
      updateHoveredClipId(null);

      // maintain the moved clip focused after the reorder if necessary
      if (originalIdx === focusedClipIndex) {
        updateFocusedClipIndex(newIdx);
      }
    }, animationCompleteMs);
  };

  const createAnimatedClone = (
    origEl: HTMLElement,
    editorsContainer: HTMLElement,
    animCnt: HTMLElement
  ): HTMLElement => {
    // create a copy of an element that we can position
    // absolutely on the page and animate movement

    const origPosition = getRelativePosition(origEl, editorsContainer);
    const cloneEl = origEl.cloneNode(true) as HTMLElement;

    cloneEl.className += ` ${styles.animated}`;
    cloneEl.style.top = `${origPosition.top}px`;
    cloneEl.style.left = `${origPosition.left}px`;
    cloneEl.style.height = `${origEl.clientHeight}px`;
    cloneEl.style.width = `${origEl.clientWidth}px`;

    // must change the ids in SVG otherwise they don't get rendered
    replaceSvgIds(cloneEl);

    animCnt.appendChild(cloneEl);

    return cloneEl;
  };

  const replaceSvgIds = (cloneEl: HTMLElement): void => {
    cloneEl
      .querySelectorAll('svg defs mask')
      .forEach(mask => (mask.id += '_clone'));

    cloneEl.querySelectorAll('[mask]').forEach(el => {
      const mask = el.attributes.getNamedItem('mask');

      if (mask) {
        // the mask attribute contains a url, like "url(mask-id-here)"
        mask.value = mask.value.replace(')', '_clone)');
      }
    });
  };

  const allowReorderingFocusedClip = hoveredClipId === null;

  return (
    <Arrange justifyContent="stretch" autoFlow="row" id="edit-tools">
      <Spacer top="large" />
      <TrimControlBar {...trimControlBarProps} />
      <Spacer top="small" />
      <div ref={editorsContainerRef} className={styles.clipEditorsContainer}>
        <Arrange
          id="clipEditorGrid"
          gap="large"
          columns={clips.map(
            clip =>
              `${Math.max(
                clip.video_properties.duration / allClipsDuration,
                MIN_CLIP_FRACTION_SIZE
              )}fr`
          )}
        >
          {clips.map((clip, idx) => {
            const allowReordering =
              clips.length > 1 &&
              ((allowReorderingFocusedClip && focusedClipIndex === idx) ||
                hoveredClipId === clip.id);

            return (
              <ClipEditor
                key={clip.id}
                clip={clip}
                idx={idx}
                onClick={handleClick}
                onReordering={onClipReordering}
                allowReordering={allowReordering}
                currentTime={
                  typeof requestedCurrentTime === 'number'
                    ? requestedCurrentTime
                    : currentTime
                }
                recentlyAddedInfo={
                  recentlyAddedInfo?.clipIds[0] == clip.id && (
                    <div>
                      {recentlyAddedInfo.clipIds.length}{' '}
                      {recentlyAddedInfo.clipIds.length == 1 ? 'clip' : 'clips'}{' '}
                      added from <strong>{recentlyAddedInfo.name}</strong>
                    </div>
                  )
                }
              />
            );
          })}
        </Arrange>
      </div>
      <EditInformation
        containerRef={editInformationContainerRef}
        duration={duration}
        durationChanged={durationChanged}
        hasBeenUpdated={hasBeenUpdated}
        isOverVideoDurationLimits={isOverVideoDurationLimits}
        onHasTrims={hasTrims}
        onExitWithoutSaving={exitWithoutSaving}
        trimVideoDuration={trimVideoDuration}
        videoId={videoId}
      />
    </Arrange>
  );
};

export default EditTools;
